import { HorizontalDivider, SegmentedButton } from '@smartgift/frontend-toolkit';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { configs } from '@hero/configs';
import {
  COLLECTION_DESC,
  DIGITAL_WRAPPER,
  GIFT_MESSAGE,
  EMAIL,
  LOGO,
  CARDISLE_GREETING_CARD
} from '@hero/constants';
import { PageLayout } from '@hero/layouts';
import { GiftExperiencePreview } from '@hero/molecules';
import { DigitalWrapper } from '@hero/organisms';
import { getAddonData } from '@hero/utils';

import {
  CollectionDescription,
  Logo,
  MessageExperience,
  MessageForm,
  SubjectLine
} from '../Components';

import * as S from './style';

const tabHeight = 80;

const noop = () => {};

function useThrottledOnScroll(callback, delay, element) {
  const throttledCallback = React.useMemo(
    () => (callback ? throttle(callback, delay) : noop),
    [callback, delay]
  );

  React.useEffect(() => {
    if (throttledCallback === noop) return undefined;

    element?.addEventListener('scroll', throttledCallback);
    return () => {
      element?.removeEventListener('scroll', throttledCallback);
      throttledCallback.cancel();
    };
  }, [throttledCallback]);
}

function CreationStepExperience({
  messageParams,
  branding,
  onUpdateBranding,
  shortcutTagsActive,
  onUpdateOccasion,
  onUpdateMessageParams,
  onUpdateAddons,
  renderCustomizeMessageModal,
  customMessageLength,
  defaultOccasion,
  onUpdateSharingMethod,
  subjectEnabled,
  sharingMethod,
  giftMessageType,
  activeGiftMessage,
  setActiveGiftMessage,
  addons
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(
    configs?.experience?.greetingCardEnabled ? GIFT_MESSAGE : LOGO
  );
  const clickedRef = React.useRef(false);
  const unsetClickedRef = React.useRef(null);
  const itemsClientRef = React.useRef([]);
  const scrollContainerNode = document.querySelector(`${PageLayout.S.PageLayoutContent}`);

  const tabIds = useMemo(() => {
    let ids = [LOGO, GIFT_MESSAGE, EMAIL, DIGITAL_WRAPPER, COLLECTION_DESC];

    if (configs?.experience?.greetingCardEnabled) {
      ids = [GIFT_MESSAGE, LOGO, EMAIL, DIGITAL_WRAPPER, COLLECTION_DESC];
    }
    if (!subjectEnabled) {
      ids = ids.filter(i => i !== EMAIL);
    }
    if (!configs?.advanceSettings?.digitalWrapperEnabled) {
      ids = ids.filter(i => i !== DIGITAL_WRAPPER);
    }

    return ids;
  }, [configs]);

  let tabWithNodes = tabIds.map(tab => {
    return {
      hash: tab,
      node: document.getElementById(tab)
    };
  });

  function changeActiveTab(hash, scrollOptions = { behavior: 'smooth' }) {
    clickedRef.current = true;
    unsetClickedRef.current = setTimeout(() => {
      clickedRef.current = false;
    }, 1000);

    if (activeTab !== hash) {
      setActiveTab(hash);

      if (scrollContainerNode) {
        scrollContainerNode.scrollTo({
          top: document.getElementById(hash)?.offsetTop - 160,
          ...scrollOptions
        });
      }
    }
  }

  const findActiveIndex = React.useCallback(() => {
    if (clickedRef.current) return;

    let active;
    for (let i = itemsClientRef.current.length - 1; i >= 0; i -= 1) {
      // No hash if we're near the top of the page
      if (scrollContainerNode.scrollTop < 0) {
        active = { hash: null };
        break;
      }

      const item = itemsClientRef.current[i];

      if (
        item.node &&
        item.node.offsetTop - 100 <
          scrollContainerNode.scrollTop + scrollContainerNode.clientHeight / 8 + tabHeight
      ) {
        active = item;
        break;
      }
    }

    if (active && activeTab !== active.hash) {
      setActiveTab(active.hash);
    }
  }, [activeTab, tabWithNodes]);

  useThrottledOnScroll(tabWithNodes.length > 0 ? findActiveIndex : null, 166, scrollContainerNode);

  const getActiveIndex = () => {
    if (configs?.experience?.greetingCardEnabled) {
      return tabIds.findIndex(t => t === activeTab);
    }
    return tabIds.findIndex(t => t === activeTab) || 1;
  };

  useEffect(() => {
    return () => {
      clearTimeout(unsetClickedRef.current);
    };
  }, []);

  useEffect(() => {
    itemsClientRef.current = tabWithNodes;
  }, [tabWithNodes]);

  useEffect(() => {
    if (scrollContainerNode) scrollContainerNode.scrollTop = 0;
  }, [scrollContainerNode]);

  const experienceTabs = [
    ...(configs?.experience?.greetingCardEnabled
      ? [
          {
            value: GIFT_MESSAGE,
            label: t('giftMessage'),
            children: (
              <>
                <MessageExperience
                  messageParams={messageParams}
                  shortcutTagsActive={shortcutTagsActive}
                  onUpdateMessageParams={onUpdateMessageParams}
                  onUpdateOccasion={onUpdateOccasion}
                  customMessageLength={customMessageLength}
                  renderCustomizeMessageModal={renderCustomizeMessageModal}
                  onUpdateAddons={onUpdateAddons}
                  giftMessageType={giftMessageType}
                  addons={addons}
                  activeGiftMessage={activeGiftMessage}
                  setActiveGiftMessage={setActiveGiftMessage}
                />
                <HorizontalDivider $margin="24px 0" />
              </>
            )
          }
        ]
      : []),
    ...(!configs?.experience?.greetingCardEnabled
      ? [
          {
            value: GIFT_MESSAGE,
            label: t('giftMessage'),
            children: (
              <>
                <MessageForm
                  messageParams={messageParams}
                  shortcutTagsActive={shortcutTagsActive}
                  onUpdateMessageParams={onUpdateMessageParams}
                  onUpdateOccasion={onUpdateOccasion}
                  customMessageLength={customMessageLength}
                  renderCustomizeMessageModal={renderCustomizeMessageModal}
                />
                <HorizontalDivider $margin="24px 0" />
              </>
            )
          }
        ]
      : []),
    ...(configs?.advanceSettings?.digitalWrapperEnabled
      ? [
          {
            value: DIGITAL_WRAPPER,
            label: t('digitalWrapper'),
            children: (
              <>
                <DigitalWrapper
                  branding={branding}
                  onUpdateData={onUpdateBranding}
                  defaultOccasion={defaultOccasion}
                />
                <HorizontalDivider $margin="24px 0" />
              </>
            )
          }
        ]
      : []),
    {
      value: LOGO,
      label: t('logo'),
      children: (
        <>
          <Logo
            branding={branding}
            onUpdateData={branding => {
              onUpdateBranding(branding);
            }}
          />
          <HorizontalDivider $margin="24px 0" />
        </>
      )
    },
    ...(subjectEnabled
      ? [
          {
            value: EMAIL,
            label: t('email'),
            children: (
              <>
                <SubjectLine
                  subject={sharingMethod?.params?.subject}
                  setSubject={subject => onUpdateSharingMethod({ params: { subject } })}
                />
                <HorizontalDivider $margin="24px 0" />
              </>
            )
          }
        ]
      : []),
    {
      value: COLLECTION_DESC,
      label: t('collectionDescription'),
      children: (
        <CollectionDescription
          messageParams={messageParams}
          onUpdateMessageParams={onUpdateMessageParams}
        />
      )
    }
  ];

  return (
    <S.Experience
      tabContentPadding={
        scrollContainerNode?.clientHeight ? scrollContainerNode.clientHeight - 297 : 0
      }
    >
      <SegmentedButton
        isAnchoring
        updateHash={false}
        activeTab={activeTab}
        onChange={changeActiveTab}
        withLine
        className="experience-tabs"
        options={experienceTabs}
      />
      <GiftExperiencePreview
        branding={branding}
        messageParams={messageParams}
        subject={sharingMethod?.params?.subject}
        activeIndex={getActiveIndex()}
        setActiveIndex={activeIndex => {
          changeActiveTab(tabIds[activeIndex], { behavior: 'auto' });
        }}
        selectedGreetingCard={getAddonData(addons, CARDISLE_GREETING_CARD)}
        activeGiftMessage={activeGiftMessage}
        setActiveGiftMessage={setActiveGiftMessage}
      />
    </S.Experience>
  );
}

CreationStepExperience.S = S;

CreationStepExperience.propTypes = {
  messageParams: PropTypes.shape({
    message: PropTypes.string,
    aboutMessage: PropTypes.string,
    senderName: PropTypes.string
  }),
  branding: PropTypes.shape({
    logoUrl: PropTypes.string
  }),
  onUpdateBranding: PropTypes.func,
  shortcutTagsActive: PropTypes.bool,
  onUpdateOccasion: PropTypes.func,
  onUpdateMessageParams: PropTypes.func,
  onUpdateAddons: PropTypes.func,
  renderCustomizeMessageModal: PropTypes.func,
  customMessageLength: PropTypes.number,
  defaultOccasion: PropTypes.string,
  onUpdateSharingMethod: PropTypes.func,
  subjectEnabled: PropTypes.bool,
  sharingMethod: PropTypes.shape({ params: PropTypes.shape({ subject: PropTypes.string }) }),
  giftMessageType: PropTypes.string,
  addons: PropTypes.array,
  activeGiftMessage: PropTypes.string,
  setActiveGiftMessage: PropTypes.func
};

export { CreationStepExperience };
